import React from "react";
import {
  ButtonComp,
  FormikDatePicker,
  MoneyInputComp,
  SelectComp2,
} from "../../../components";
import { Form, Formik } from "formik";
import { AiOutlineClose } from "react-icons/ai";
import * as Yup from "yup";
import { useEnums } from "../../../hooks/useEnums";
import { useGetAllBankInfoQuery } from "../../../store/admin/settings/societyBankSlice";
import { useRecordLoanRepaymentMutation } from "../../../store/admin/loanRepayment";
import toast from "react-hot-toast";
import { PaystackButton } from "react-paystack";
import moment from "moment";
import { formatMoney } from "../../../utils";
import {
  installmentBalance,
  paymentEffect,
  validatePaymentAmount,
} from "../../../components/Loan/partialRepayment";

export default function LoanRepaymentRecord({
  Back,
  loanRepaymentDetails,
  partialEnabled = false,
  loanOutstanding,
  lateFeeBalance = 0,
}) {
  const enums = useEnums();
  const { data, isLoading: isLoadingBank } = useGetAllBankInfoQuery({ pageNumber: 1 });
  const bankOptions = data?.company_banks?.map((item) => {
    return {
      label: item.bank_name,
      value: item.bank_name,
    };
  });

  // What is still owed on this installment (the full amount until part payments are made)
  const installmentLeft = installmentBalance(loanRepaymentDetails);
  // Late fees on the loan are cleared first, so clearing this installment takes both
  const feesOwed = Math.max(0, Number(lateFeeBalance) || 0);
  const balance = Math.round((installmentLeft + feesOwed) * 100) / 100;
  const amountPaidSoFar = parseFloat(loanRepaymentDetails?.amount_paid || 0);

  const initialValues = {
    bank: "",
    amount: String(balance),
    payment_method: "",
    payment_date: moment().format("YYYY-MM-DD"), // Set to current date for real-time payments
  };
  
  // Function to check if payment method requires bank
  const requiresBank = (paymentMethod) => {
    if (!paymentMethod) return false;
    const methodUpper = paymentMethod.toUpperCase();
    return methodUpper.includes("BANK") || methodUpper.includes("TRANSFER") || methodUpper === "CHEQUE";
  };
  
  const validationSchema = Yup.object({
    bank: Yup.string().when("payment_method", {
      is: (value) => requiresBank(value),
      then: (schema) => schema.required("Bank is required for this payment method"),
      otherwise: (schema) => schema.notRequired(),
    }),
    amount: Yup.string()
      .required("Required")
      .test("valid-amount", function (value) {
        const message = validatePaymentAmount({
          amount: value,
          balance,
          outstanding: loanOutstanding,
          enabled: partialEnabled,
        });
        return message ? this.createError({ message }) : true;
      }),
    payment_date: Yup.string().required("Required"),
    payment_method: Yup.string().required("Required"),
  });

  const handlePaystackSuccessAction = (reference) => {
    // Implementation for whatever you want to do with reference and after success call.
    console.log(reference);
  };

  const handlePaystackCloseAction = () => {
    // implementation for  whatever you want to do when the Paystack dialog closed.
    console.log("closed");
  };

  const [recordRepayment, { isLoading }] = useRecordLoanRepaymentMutation();

  const handleFormSubmit = async (values, { resetForm }) => {
    // Only include bank in payload if payment method requires it
    const payload = {
      amount: values.amount,
      payment_method: values.payment_method,
      payment_date: values.payment_date,
      breakdown_id: loanRepaymentDetails.id,
      loan_request_id: loanRepaymentDetails.loan_request_id,
    };
    
    // Only add bank if payment method requires it
    if (requiresBank(values.payment_method) && values.bank) {
      payload.bank = values.bank;
    }
    
    console.log(payload);
    const response = await recordRepayment(payload);
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Success");
      resetForm();
      Back();
    }
  };

  return (
    <>
      <section className="w-full px-4">
        <div className="flex mb-6 py-3 justify-center items-center relative border-b  ">
          <AiOutlineClose
            size={20}
            onClick={Back}
            className=" pointer absolute top-7 left-4  "
          />
          <h4 className="fw-bold mb-0 text-black1 text-[1.5rem]">
            Loan Record
          </h4>
        </div>

        <div className="w-full">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
            className="w-full"
          >
            {({ values, errors, touched, setFieldValue }) => {
              // Check if bank field should be visible based on payment method
              // Payment methods that require bank: BANK_TRANSFER, BANK TRANSFER, CHEQUE, Cheque
              const requiresBank = (paymentMethod) => {
                if (!paymentMethod) return false;
                const methodUpper = paymentMethod.toUpperCase();
                return methodUpper.includes("BANK") || methodUpper.includes("TRANSFER") || methodUpper === "CHEQUE";
              };
              
              const showBankField = requiresBank(values.payment_method);
              
              return (
              <Form className="w-full space-y-4">
                <div
                  className="rounded p-3 mb-2"
                  style={{ backgroundColor: "#f7f7f7" }}
                >
                  <div className="row text-center g-2">
                    <div className="col-4">
                      <small className="text-muted d-block">Installment</small>
                      <span className="fw-bold">
                        {formatMoney(parseFloat(loanRepaymentDetails?.amount || 0))}
                      </span>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Paid so far</small>
                      <span className="fw-bold">{formatMoney(amountPaidSoFar)}</span>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Balance</small>
                      <span className="fw-bold text-primary1">{formatMoney(installmentLeft)}</span>
                    </div>
                  </div>
                  {feesOwed > 0 && (
                    <p className="text-danger small text-center mb-0 mt-2">
                      Late fees of {formatMoney(feesOwed)} are cleared first, so {formatMoney(balance)} clears this installment
                    </p>
                  )}
                  {partialEnabled && loanOutstanding != null && (
                    <p className="text-muted small text-center mb-0 mt-2">
                      Total still owed on this loan: {formatMoney(parseFloat(loanOutstanding))}
                    </p>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                  <div className="">
                    <SelectComp2
                      LabelClassName={"fw-3"}
                      placeholder={"Select payment type"}
                      labelText={"Payment Method"}
                      arrayComp={enums["PaymentMethod"] ?? []}
                      selectedOption={values.payment_method}
                      setSelectedOption={(selectedOption) => {
                        const selectedValue = selectedOption?.value || "";
                        setFieldValue("payment_method", selectedValue);
                        // Clear bank field if payment method doesn't require it
                        if (!requiresBank(selectedValue)) {
                          setFieldValue("bank", "");
                        }
                        // For real-time payments (Paystack), set payment date to current date
                        if (selectedValue === "PAYSTACK") {
                          setFieldValue("payment_date", moment().format("YYYY-MM-DD"));
                        }
                      }}
                      errors={
                        touched.payment_method && errors.payment_method
                          ? [errors.payment_method]
                          : []
                      }
                    />
                  </div>
                  {showBankField && (
                    <div className="">
                      <SelectComp2
                        LabelClassName={"fw-3"}
                        placeholder={"Select bank tranferred to"}
                        labelText={"Bank Paid to"}
                        arrayComp={bankOptions}
                        isLoading={isLoadingBank}
                        selectedOption={values.bank}
                        setSelectedOption={(selectedOption) => {
                          setFieldValue("bank", selectedOption?.value || "");
                        }}
                        errors={touched.bank && errors.bank ? [errors.bank] : []}
                      />
                    </div>
                  )}
                  <div className="">
                    <MoneyInputComp
                      LabelText={"Amount"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-3"}
                      placeholder={"Enter amount"}
                      descClassName="text-muted"
                      name={"amount"}
                      disabled={!partialEnabled}
                      desc={paymentEffect({
                        amount: values.amount,
                        balance,
                        enabled: partialEnabled,
                      })}
                    />
                  </div>
                  <div>
                    <FormikDatePicker
                      fieldName={"payment_date"}
                      labelText={"Payment date"}
                      labelClassName={"fw-3"}
                      disabled={values.payment_method === "PAYSTACK"}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-center  pb-4">
                  <div className="w-[12rem]">
                    {values.payment_method === "PAYSTACK" ? (
                      <PaystackButton
                        className="w-100 bg-primary1 py-[10px] text-white border-0 rounded fw-3"
                        publicKey="pk_test_69503f337ce58c28be91c44aab7db1fee0be467f"
                        email="user@example.com"
                        amount={Math.round(parseFloat(values.amount || 0) * 100)}
                        reference={new Date().getTime().toString()}
                        onSuccess={(reference) => {
                          console.log("Payment successful:", reference);
                          // After Paystack success, submit the form with the reference
                          handleFormSubmit(
                            {
                              ...values,
                              paystack_transaction_reference: reference.reference,
                              payment_date: moment().format("YYYY-MM-DD"),
                            },
                            { resetForm: () => {} }
                          );
                        }}
                        onClose={() => {
                          console.log("Paystack dialog closed");
                        }}
                        text="Pay with Paystack"
                      />
                    ) : (
                      <ButtonComp
                        loading={isLoading}
                        type={"submit"}
                        btnText={"Record loan"}
                        btnClassName={
                          "w-100 bg-primary1 py-[10px] text-white border-0 rounded fw-3"
                        }
                      />
                    )}
                  </div>
                </div>
              </Form>
              );
            }}
          </Formik>
          {/* <PaystackButton className="bg-[red]" {...componentProps} /> */}
        </div>
      </section>
    </>
  );
}
